import { SWRConfiguration } from "swr";

import { FetcherResult } from "../api-client/fetcher";
import { ModifiedSWRResponse, useSwrGet } from "../api-client/useSwrGet";
import { useDealerOrderModal } from "../blocks/Cart/DealerOrderModal/DealerOrderModal";
import { useOrderDetails } from "../blocks/Cart/useOrderDetails";
import { LanguageMap } from "../i18n/useLanguage";
import { useNullableDealerAccountInfo } from "../stores/hooks/useNullableDealerAccountInfo";
import { OrderItemTire, VehicleInformation } from "../stores/slices/cartSlice";

export const usePreviewDealerOrderFromCart = (
  swrOptions: SWRConfiguration<FetcherResult<DealerOrderPreview>>,
  { disabled }: { disabled: boolean }
): ModifiedSWRResponse<
  FetcherResult<DealerOrderPreview>,
  DealerOrderPreview
> => {
  const [modalIsOpen] = useDealerOrderModal();
  const { data: cart } = useOrderDetails();
  const dealer = useNullableDealerAccountInfo();

  const shouldFetch = Boolean(modalIsOpen && cart) && dealer && !disabled;

  const { data, ...result } = useSwrGet<DealerOrderPreview>(
    shouldFetch
      ? "	/retailSiteWebService/shopping/dealerOrder/previewDealerOrderFromCart.php"
      : null,
    {
      dealerId: dealer?.dealerId ?? "",
      orderNumber: cart?.orderNumber ?? "",
    },
    swrOptions
  );

  const safeData = Array.isArray(data) ? undefined : data;

  return { ...result, data: safeData };
};

type ConsumerData = {
  consumerAttributes: unknown[];
  consumerBillTo: unknown[];
  consumerShipTo: unknown[];
};

export type Fulfillment = {
  estimatedDelivery: string;
  freight: string;
  quantity: number;
  shipMethod: string;
  sourceDC: string;
  sourceDCName: string;
  status?: string;
};

type Product = {
  ATDProductNumber: string;
  CustomerPartNumber: string;
  MFGProductNumber: string;
  brand: string;
  cost: string;
  description: string;
  fet: string;
  local: string;
  localPlus: string;
  msrp: string;
  nationWide: string;
  productGroup: string;
  productSpec: unknown[];
  retail: string;
  specialDiscount: string;
  style: string;
};

export type FullfilmentOrderLine = {
  ATDProductNumber: string;
  addOns: unknown[];
  billTo: string;
  cartLineNumber: number;
  cost: string;
  description: string;
  discontinued: boolean;
  fet: string;
  fulfillments: {
    fulfillments: Fulfillment[];
  };
  lineTotal: string;
  product: Product;
  quantity: number;
  quickShip: string;
  replaced: boolean;
  statusMessages: unknown[];
};

export type OrderFulfillment = {
  created_timestamp: string;
  fulfillment_key: string;
  fulfillment_type: string;
  fulfillment_value: {
    consumerData: ConsumerData;
    customerPoNumber: string;
    fillKill: string;
    isTest: string;
    localPlus: string;
    orderLines:
      | {
          orderLine: FullfilmentOrderLine[];
        }
      | FullfilmentOrderLine[];
    orderNumber?: string;
    orderTotal: string;
    orderType: string;
    pickup: string;
  };
  is_valid: boolean;
};

export type DealerOrderPreview = {
  appointmentRequestDate: string;
  appointmentRequestTime: string;
  customerAddress: string;
  customerCity: string;
  customerContactMethodPreference: string;
  customerCountry: string;
  customerEmail: string;
  customerName: string;
  customerNameBilling: string;
  customerNotes: string;
  customerPhone: string;
  customerPhoneBilling: string;
  customerPostalCode: string;
  customerState: string;
  dealerId: string;
  dealerNotes: string;
  isNational: string;
  orderAbsoluteUrl: LanguageMap;
  orderCreatedTimestamp: string;
  orderCreatedUserId: string;
  orderCreatedUserName: string;
  orderDeposit: string;
  orderDepositDealer: string;
  orderDepositInternal: string;
  orderDisclaimerList: unknown[];
  orderDueAtDealer: string;
  orderFulfillmentList: OrderFulfillment[];
  orderGrandTotal: string;
  orderId: string;
  orderIsPriceMatched: boolean;
  orderIsPrinted: boolean;
  orderItemList: OrderItemTire[];
  orderLastUpdatedTimestamp: string;
  orderMessageList: { scope: string; type: string; value: string }[];
  orderNormalRefund: string;
  orderNotes: LanguageMap;
  orderNumber: string;
  orderPaymentList: unknown[];
  orderRestockingFee: string;
  orderStatus: string;
  orderStatusDescription: LanguageMap;
  orderSubtotal: string;
  orderTaxPercent: string;
  orderTaxTotal: string;
  orderTypeCode: string;
  referenceNumber: string;
  supportConsumerOrder: boolean;
  supportDealerOrder: boolean;
  vehicleInformation: VehicleInformation[];
  workOrderNumber: string;
};
